import React, { useCallback, useEffect, useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import CheckRow from './CheckRow';
import everpassLogo from '../../assets/everpassLogo.svg';
import suitcase from '../../assets/suitcase.svg';
import gamepad from '../../assets/gamepad.svg';
import { scrollIntoViewWithinContainer } from '../../epg/utils/shared/scrollIntoView';
import { getModelConfig } from '../../utils/models';
import './ReadinessBlock.scss';

const AUTO_DISMISS_MS = 60000;
const TICK_MS = 1000;

const NON_PASS_STATUSES = new Set(['fail', 'at_risk', 'skipped']);

function getQrValue(inactiveState) {
  const haystack = `${inactiveState.email || ''} ${inactiveState.contact || ''}`;
  const match = haystack.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/);
  return match ? `mailto:${match[0]}` : 'mailto:support@everpass.com';
}

/**
 * Full-screen overlay shown when boot checks finish At-Risk or Not-Ready.
 * Left column: support info + QR. Right column: scrollable check-card list.
 * Auto-dismisses after 60s. Capture-phase keydown beats EPG/norigin.
 */
export default function ReadinessBlock({ checks, overallStatus, onDismiss }) {
  const visibleChecks = checks.filter(c => NON_PASS_STATUSES.has(c.status));
  const CLOSE_IDX = visibleChecks.length;
  const itemCount = CLOSE_IDX + 1;

  const [secondsLeft, setSecondsLeft] = useState(Math.round(AUTO_DISMISS_MS / 1000));
  const dismissCalled = useRef(false);
  const rowRefs = useRef([]);
  const [focusIdx, setFocusIdx] = useState(CLOSE_IDX);
  const focusIdxRef = useRef(CLOSE_IDX);

  const dismiss = useCallback(() => {
    if (dismissCalled.current) return;
    dismissCalled.current = true;
    onDismiss();
  }, [onDismiss]);

  // Countdown ticker
  useEffect(() => {
    const interval = setInterval(() => {
      setSecondsLeft(s => s - 1);
    }, TICK_MS);
    return () => clearInterval(interval);
  }, []);

  // Auto-dismiss
  useEffect(() => {
    const timer = setTimeout(dismiss, AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [dismiss]);

  // Capture-phase keydown registered before EPG/norigin loads.
  // Same-phase same-target listeners fire in registration order, so our handler
  // runs first. stopImmediatePropagation prevents EPG from seeing the key.
  useEffect(() => {
    const handleKey = (e) => {
      if (!['ArrowDown', 'ArrowUp', 'Enter', ' ', 'Return', 'Accept'].includes(e.key)) return;
      e.stopPropagation();
      e.stopImmediatePropagation();
      e.preventDefault();
      if (e.key === 'ArrowDown') {
        setFocusIdx(i => { focusIdxRef.current = Math.min(i + 1, itemCount - 1); return focusIdxRef.current; });
      } else if (e.key === 'ArrowUp') {
        setFocusIdx(i => { focusIdxRef.current = Math.max(i - 1, 0); return focusIdxRef.current; });
      } else if (focusIdxRef.current === itemCount - 1) {
        dismiss();
      }
    };
    window.addEventListener('keydown', handleKey, { capture: true });
    return () => window.removeEventListener('keydown', handleKey, { capture: true });
  }, [dismiss, itemCount]);

  // Scroll focused row into view when focusIdx moves between rows
  useEffect(() => {
    if (focusIdx < CLOSE_IDX && rowRefs.current[focusIdx]) {
      scrollIntoViewWithinContainer(rowRefs.current[focusIdx], 'RkBlock__list', 40);
    }
  }, [focusIdx, CLOSE_IDX]);

  const inactiveState = getModelConfig().inactiveState;
  const qrValue = getQrValue(inactiveState);
  const isNotReady = overallStatus === 'Not-Ready';
  const statusMod = isNotReady ? 'RkBlock--not-ready' : 'RkBlock--at-risk';

  return (
    <div className={`RkBlock ${statusMod}`} role="alertdialog" aria-label="Contact Support">
      <div className="RkBlock__body">
        <div className="RkBlock__left">
          <div className="RkBlock__top">
            <p className="RkBlock__eyebrow">
              <img className="RkBlock__eyebrow-icon" src={suitcase} alt="" />
              Streaming Diagnostic
              <span className="RkBlock__badge">
                {isNotReady ? 'Not Ready' : 'At Risk'}
              </span>
            </p>
            <h1 className="RkBlock__headline">Contact Support</h1>
            <p className="RkBlock__support-text">
              Scan the QR code with your phone <strong>to call our team</strong>
            </p>
            <p className="RkBlock__support-text">
              or email us to <strong>support@everpass.com</strong>
            </p>
          </div>
          <div className="RkBlock__qr">
            <QRCodeSVG value={qrValue} size={220} level="M" bgColor="#ffffff" fgColor="#000000" />
          </div>
        </div>

        <div className="RkBlock__right">
          <ul className="RkBlock__list">
            {visibleChecks.map((check, i) => (
              <CheckRow
                key={check.id}
                ref={el => { rowRefs.current[i] = el; }}
                check={check}
                focused={focusIdx === i}
              />
            ))}
          </ul>
        </div>
      </div>

      <div className="RkBlock__footer">
        <div className="RkBlock__footer-left">
          <img className="RkBlock__logo" src={everpassLogo} alt="EverPass" />
        </div>
        <div className="RkBlock__footer-right">
          <span className="RkBlock__countdown">Auto-closing in {secondsLeft}s</span>
          <span className="RkBlock__select-hint" aria-hidden="true">
            <img className="RkBlock__select-icon" src={gamepad} alt="" />
            Select
          </span>
          <button
            className={`RkBlock__close${focusIdx === CLOSE_IDX ? ' RkBlock__close--focused' : ''}`}
            onClick={dismiss}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
